/**
 * Enum representing termination reasons in UK employment
 */
export enum TerminationReason {
  RESIGNATION = "RESIGNATION",
  MUTUAL_AGREEMENT = "MUTUAL_AGREEMENT",
}

/**
 * Interface for tracking value changes with timestamp
 */
interface ValueWithTimestamp<T> {
  value: T;
  timestamp: string;
  reason?: string;
}

/**
 * Interface for notice period calculation
 */
export interface NoticeCalculation {
  requiredNoticeDays: number;
  payThroughDate: string;
  workingNoticePeriod: boolean;
  paymentInLieuOfNotice: boolean;
  notice_period_start_date?: string | null;
  notice_period_end_date?: string | null;
}

/**
 * Interface for a component of final pay
 */
export interface FinalPayComponent {
  id: string;
  description: string;
  amount: number;
  type: "payment" | "deduction";
  pay_type?: string;
  type_code?: string;
}

/**
 * Interface for the full termination record
 */
export interface TerminationRecord {
  id?: string;
  employee_id: string;
  termination_reason?: TerminationReason;
  termination_date: string;
  employmentStartDate?: string;
  noticeCalculation?: NoticeCalculation;
  final_pay_components?: FinalPayComponent[];
  created_at?: string;
  workflow_status?:
    | "draft"
    | "pending"
    | "approved"
    | "processed"
    | "cancelled";
}

/**
 * Request interfaces for API calls
 */
export interface CreateTerminationRequest {
  employee_id: string;
  termination_reason?: TerminationReason;
  termination_date: string;
  final_working_day?: string;
}

export interface UpdateTerminationRequest {
  termination_reason?: TerminationReason;
  termination_date?: string;
  notice_calculation?: NoticeCalculation;
  final_pay_components?: FinalPayComponent[];
  notice_period_start_date?: string;
  notice_period_end_date?: string;
  is_payment_in_lieu_of_notice?: boolean;
}

export interface SubmitTerminationRequest {
  terminationId: string;
}

/**
 * Response interfaces for API calls
 */
export interface GetTerminationResponse {
  termination: TerminationRecord;
}

export interface CreateTerminationResponse {
  terminationId: string;
  termination: TerminationRecord;
}

export interface UpdateTerminationResponse {
  termination: TerminationRecord;
}

export interface SubmitTerminationResponse {
  success: boolean;
  termination: TerminationRecord;
}

export interface TerminationResponse {
  termination: TerminationRecord | null;
  success: boolean;
  message?: string;
  final_pay_components?: FinalPayComponent[];
}
